import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useOperationsStore, workInstructionService } from '@tom-ui/operations'
import { ConfirmationDialog, useMinimalsTheme } from '@tom-ui/ui'
import { ContainerMovementInfo, useUtilsStore } from '@tom-ui/utils'
import { JourneyPosition, reservationService, YardPositionSelector } from '@tom-ui/yard'
import { observer } from 'mobx-react-lite'
import StackedAlert from 'modules/storage/src/components/StackedAlert'
import { useContainerTurnoversStores } from 'modules/storage/src/pages/container-turnovers/hooks/container-turnovers-stores.hook'
import { AppAlert } from 'modules/storage/src/stores/alert.store'
import { useMemo } from 'react'
import {
  ContainerMovementDialogViewStore,
  InternalMovementServiceCalls,
} from './container-movement-dialog.view-store'

export const ContainerMovementDialog = observer(() => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const { containerMovementDialogStore } = useUtilsStore()
  const { appStore } = useOperationsStore()

  const { containerTurnoversListStore } = useContainerTurnoversStores()

  const viewStore = useMemo(() => {
    const serviceCalls: InternalMovementServiceCalls = {
      yardCreateReservation: request => reservationService.create(request),
      operationsCreateInternalWorkInstruction: workInstructionService.createInternalWorkInstruction,
    }

    return new ContainerMovementDialogViewStore(containerMovementDialogStore, serviceCalls)
  }, [containerMovementDialogStore])

  const handleClose = () => {
    viewStore.reset()
    containerMovementDialogStore.closeDialog()
  }

  const handleSubmit = async () => {
    try {
      appStore.increaseLoadingCounter()

      await viewStore.submit()
      await containerTurnoversListStore.reload(true)

      viewStore.reset()
      containerMovementDialogStore.closeDialog()
      appStore.setShowAlert('success', t('containerMoveCreated', 'Container movement created'))
    } catch {
    } finally {
      appStore.decreaseLoadingCounter()
    }
  }

  const handleDestinationChange = (destination?: JourneyPosition) => {
    viewStore.setDestination(destination)
  }

  const getContainerOptionLabel = (option: ContainerMovementInfo) => {
    return option.containerNumber
  }

  const getAlertMessages = () => {
    return viewStore.alerts.map((alert, index) => {
      const message = t(
        'unhandledContainerMovementAlertType',
        'Unhandled container movement alert type',
      )

      const appAlert: AppAlert = {
        key: index.toString(),
        message,
        severity: 'warning',
        date: new Date(),
      }

      return appAlert
    })
  }

  return (
    <ConfirmationDialog
      open={containerMovementDialogStore.isOpen}
      title={t('containerMovement', 'Container movement')}
      primaryActionText={t('move', 'Move')}
      closeLabel={t('cancel', 'Cancel')}
      primaryActionDisabled={viewStore.destination === undefined}
      maxWidth='md'
      onClose={handleClose}
      onConfirm={handleSubmit}
      primaryActionDataCy='confirm-move-container-btn'
    >
      <Stack gap={theme.customSpacing.m}>
        {containerMovementDialogStore.containers.length > 1 && (
          <Typography>
            {t(
              'containerMovementAmountLabel',
              'You have {amount} containers to be moved. You can still change this amount before submit',
              {
                amount: containerMovementDialogStore.containers.length,
              },
            )}
          </Typography>
        )}

        <Autocomplete
          multiple
          options={containerMovementDialogStore.containers}
          value={containerMovementDialogStore.containers}
          renderInput={params => (
            <TextField {...params} label={t('containerNumbers', 'Container numbers')} />
          )}
          getOptionLabel={getContainerOptionLabel}
          isOptionEqualToValue={(option, value) => value.containerNumber === option.containerNumber}
          disabled
        />

        <YardPositionSelector
          journeyId={containerMovementDialogStore.selectedContainer?.containerJourneyId}
          onChange={position => handleDestinationChange(position)}
        />

        {viewStore.alerts.length > 0 && <StackedAlert alerts={getAlertMessages()} />}
      </Stack>
    </ConfirmationDialog>
  )
})
